// @fileoverview : 영업관리 > 배차

import { mutateTransportation } from "@/api/transportation/useTransportationQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/DetailForm";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import { TransportationCreateForm } from "@/features/transportation/form/CreateForm";
import { TransportationStatementForm } from "@/features/transportation/form/StatementForm";
import { TransportationStatusForm } from "@/features/transportation/form/StatusForm";
import { TransportationDetailForm } from "@/features/transportation/form/TransportationDetailForm";
import { TransportationUpdateForm } from "@/features/transportation/form/UpdateForm";
import customAlert from "@/features/ui/alert/Alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { VehicleDetailForm } from "@/features/vehicle/form/DetailForm";
import { setTransportationStatus, transportationStatusColor } from "@/utils/scmDataStatus";
import timeUtil from "@/utils/timeUtil";
import { ActionIcon, Avatar, Badge, Button, Flex, Text, Tooltip } from "@mantine/core";
import {
  ScmApiTransportationTransportationIdDeleteRequest,
  TransportationGet200Response,
  TransportationGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import {
  IconEdit,
  IconFilterPlus,
  IconPlus,
  IconPrinter,
  IconRecycle,
  IconRefresh,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const TransportationPage = () => {
  const { openModal, closeModal } = useModal();
  const { selectedRows, setSelectedRows, refetch, resetQueryStrings } = useStandardLayout();
  const { t } = useTranslation();
  // const [isBeta] = useLocalStorage<boolean>({
  //   key: "isBeta",
  //   defaultValue: false, // 기본값은 모든 컬럼이 체크됨
  // });
  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiTransportationTransportationIdDeleteRequest) =>
      mutateTransportation
        .delete(params)
        .mutationFn(params as any | ScmApiTransportationTransportationIdDeleteRequest),
    {
      onSuccess: () => {
        refetch();
        customAlert("배차 삭제에 성공하였습니다.", "삭제 성공", "green");
      },
      onError: () => {
        customAlert("배차 삭제에 실패하였습니다.", "삭제 실패", "red");
      },
    },
  );

  const columns: readonly Column<TransportationGet200ResponseRowsInner>[] = [
    {
      ...SelectColumn,
      width: 70,
      maxWidth: 500,
      resizable: true,
    },
    {
      key: "id",
      name: t("transportation.id"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() => {
              openModal(
                <TransportationDetailForm transportationID={formatterProps.row.id} />,
                null,
                t("common.detail", { item: t("transportation.transportation") }),
              );
            }}
          >
            {formatterProps.row.id}
          </LinkAnchor>
        );
      },
    },
    {
      key: "status",
      name: t("transportation.status"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Badge
            variant="filled"
            color={
              transportationStatusColor[
                formatterProps?.row?.status as keyof typeof transportationStatusColor
              ]
            }
          >
            {t(setTransportationStatus(formatterProps?.row?.status ?? ""))}
          </Badge>
        );
      },
    },
    {
      key: "direction",
      name: t("transportation.direction"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Badge color={formatterProps.row.direction === "INBOUND" ? "blue" : "red"}>
            {t(formatterProps.row.direction ?? "")}
          </Badge>
        );
      },
    },
    {
      key: "code",
      name: t("transportation.code"),
      sortable: true,
      resizable: true,
    },
    {
      key: "scheduledAt",
      name: t("stockPlan.scheduledAt"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <Flex>
            {formatterProps.row.scheduledAt ? timeUtil(formatterProps.row.scheduledAt) : ""}
          </Flex>
        );
      },
    },
    {
      key: "partnerName",
      name: t("partner.name"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <PartnerDetailForm partnerCode={formatterProps.row.partnerCode} />,
                null,
                t("common.detail", { item: t("partner.partner") }),
              )
            }
          >
            {formatterProps.row.partner?.name ?? formatterProps.row.partnerCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "transportation.carNumber",
      name: t("transportation.carNumber"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <VehicleDetailForm code={formatterProps.row.vehicleCode} />,
                null,
                t("common.detail", { item: t("vehicle.vehicle") }),
              )
            }
          >
            {formatterProps.row.vehicle?.plateNumber}
          </LinkAnchor>
        );
      },
    },
    {
      key: "transportation.driver",
      name: t("transportation.driver"),
      sortable: true,
      resizable: true,
    },
    {
      key: "numberOfWmsLogs",
      name: t("transportation.numberOfWmsLogs"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const numberOfWmsLogs = formatterProps.row.wmsLogs?.length;
        return (
          <Flex justify={"flex-end"} align="center">
            {!numberOfWmsLogs ? "-" : numberOfWmsLogs}
          </Flex>
        );
      },
    },
    {
      key: "options",
      name: t("common.action"),
      sortable: true,
      resizable: true,
      formatter: (formatterProps) => {
        const updateTransportationActionView = () => {
          openModal(
            <TransportationUpdateForm formatterProps={formatterProps?.row} />,
            null,
            `${t("common.edit", { item: t("transportation.transportation") })}`, // 배차 수정
            true,
          );
        };

        const deleteTransportationAction = () => {
          openModal(
            <ConfirmForm
              message={t("message.rowDelete")}
              yesCallback={() =>
                deleteMutate({ transportationId: formatterProps?.row.id as number })
              }
              noCallback={() => {}}
              commonCallback={() => closeModal(null)}
            />,
            null,
            `${t("common.delete", { item: t("transportation.transportation") })}`, // 배차 삭제
          );
        };

        const detailTransportationAction = () => {
          openModal(
            <TransportationDetailForm transportationID={formatterProps.row.id} />,
            null,
            t("common.detail", { item: t("transportation.transportation") }),
          );
        };

        return (
          <Flex w="100%" h="100%" justify="center" align="center" gap="md">
            <ActionIcon color="blue" variant="subtle" onClick={detailTransportationAction}>
              <IconSearch />
            </ActionIcon>
            <ActionIcon variant="subtle" onClick={updateTransportationActionView} color="yellow">
              <IconEdit />
            </ActionIcon>
            <ActionIcon variant="subtle" onClick={() => deleteTransportationAction()} color="red">
              <IconTrash />
            </ActionIcon>
          </Flex>
        );
      },
    },
  ];

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        `${t("message.deleteSelect", { item: t("transportation.transportation") })}`,
        `${t("common.deleteFail", { item: t("transportation.transportation") })}`,
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ transportationId: JSON.parse(row).id });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      `${t("common.delete", { item: t("transportation.transportation") })}`,
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <Flex gap="sm">
          <Button
            leftIcon={<IconPlus />}
            onClick={() =>
              openModal(
                <TransportationCreateForm />,
                null,
                t("common.additional", { item: t("transportation.transportation") }),
              )
            }
          >
            {t("common.additional", { item: t("transportation.transportation") })}
          </Button>
          <Button
            leftIcon={<IconRefresh />}
            color="orange"
            disabled={!([...selectedRows].length === 1)}
            onClick={() =>
              openModal(
                <TransportationStatusForm formatterProps={JSON.parse([...selectedRows].at(0))} />,
                null,
                t("common.change", { item: t("transportation.status") }),
              ).then((result) => {
                if (result) {
                  // 로직이 정상적으로 처리되면 선택된 행을 초기화
                  setSelectedRows((): Set<any> => new Set());
                }
              })
            }
          >
            {t("common.change", { item: t("transportation.status") })}
          </Button>
          <Button
            leftIcon={<IconPrinter />}
            color="orange"
            disabled={
              !([...selectedRows].length === 1) ||
              (JSON.parse([...selectedRows].at(0)) as TransportationGet200ResponseRowsInner)
                ?.wmsLogs?.length === 0
            }
            onClick={() =>
              openModal(
                <TransportationStatementForm data={JSON.parse([...selectedRows].at(0))} />,
                null,
                "",
              )
            }
          >
            {t("transportation.statement")}
          </Button>
        </Flex>
        <Flex gap="sm">
          <Button rightIcon={<IconRecycle />} color="teal" onClick={() => resetQueryStrings()}>
            {t("common.reset")}
          </Button>
          <Button rightIcon={<IconTrash />} color="red" onClick={() => handleDelete()}>
            {t("common.rowDelete")}
          </Button>
        </Flex>
      </Flex>
      {
        <Flex px="sm" direction="column" gap="xs">
          <Text fz="xl" fw={600}>
            {t("common.list", { item: t("transportation.transportation") })}
          </Text>
          <Text fz="xs">{t("common.subTitle", { item: t("transportation.subTitle") })}</Text>
        </Flex>
      }
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<TransportationGet200Response, TransportationGet200ResponseRowsInner>
            columns={columns}
            rowKeyGetter={(row: TransportationGet200ResponseRowsInner) => JSON.stringify(row)}
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<TransportationGet200Response> />
      </Flex>
    </Flex>
  );
};
